/**
 * Où l'attaque d'un échantillon commence vraiment.
 *
 * # Ce que la mesure a montré
 *
 * Les MP3 du Salamander portent une dizaine de millisecondes de quasi-silence
 * avant le coup de marteau. Relevé le 2026-09-12 sur `public/piano`, par
 * tranches de deux millisecondes sous le pic : le do central v8 reste
 * cinquante décibels sous son pic pendant douze millisecondes puis monte d'un
 * coup ; le la0 démarre vers huit, le do7 vers dix. Même profil en v16, donc
 * c'est la prise de son et non la nuance. Et Chromium 148 rend exactement la
 * longueur qu'ffmpeg rend : ce n'est pas non plus le décodeur, qui applique
 * bien le saut d'amorce LAME.
 *
 * Ce silence est du retard pur, sur chaque note, chez tout le monde, en solo
 * comme en séance. Il ne désaligne personne — les deux machines le portent —
 * mais il s'ajoute à tout le reste, et il pèse autant que le tampon de sortie
 * d'un Mac. Le sampler de Tone démarre toujours à l'offset zéro ; c'est donc
 * le buffer qu'on rogne, une fois, au chargement (voir `sample-buffer-cache`).
 *
 * # La règle
 *
 * Le pic se cherche dans la première fenêtre — l'attaque d'un piano culmine
 * dans les cinquante millisecondes qui suivent le coup —, l'attaque est le
 * premier échantillon qui monte à moins de quarante décibels sous ce pic, et
 * on garde une milliseconde de garde devant : ce qu'il y a là est dix décibels
 * plus bas encore, donc aucun clic, et le transitoire n'est jamais entamé.
 * Deux canaux : le plus précoce l'emporte, ils sont lus ensemble.
 */

/** Seuil de l'attaque, en décibels sous le pic. */
export const ONSET_THRESHOLD_DB = -40;

/** Ce qu'on garde devant l'attaque, en secondes. */
export const ONSET_GUARD_SECONDS = 0.001;

/** Fenêtre où chercher le pic et l'attaque, en secondes. */
export const ONSET_SEARCH_SECONDS = 0.25;

/**
 * La durée du silence qui précède l'attaque, en secondes. Zéro quand il n'y
 * a rien à rogner : attaque immédiate, ou buffer entièrement silencieux.
 */
export function leadingSilenceSeconds(
  channels: readonly Float32Array[],
  sampleRate: number,
): number {
  if (channels.length === 0 || sampleRate <= 0) {
    return 0;
  }

  const window = Math.min(channels[0].length, Math.ceil(ONSET_SEARCH_SECONDS * sampleRate));
  let peak = 0;

  for (const channel of channels) {
    for (let i = 0; i < window; i += 1) {
      const magnitude = Math.abs(channel[i]);

      if (magnitude > peak) {
        peak = magnitude;
      }
    }
  }

  if (peak === 0) {
    return 0;
  }

  const threshold = peak * Math.pow(10, ONSET_THRESHOLD_DB / 20);
  let onset = window;

  for (const channel of channels) {
    for (let i = 0; i < onset; i += 1) {
      if (Math.abs(channel[i]) > threshold) {
        onset = i;
        break;
      }
    }
  }

  return Math.max(0, onset / sampleRate - ONSET_GUARD_SECONDS);
}
